import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import {
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  Drawer,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  useMediaQuery,
  ThemeProvider,
  Tooltip,
  Badge,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  MailOutline as MailOutlineIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  PeopleAlt as PeopleAltIcon,
  QueryStats as QueryStatsIcon,
  Logout as LogoutIcon,
  NotificationsActive as NotificationsActiveIcon,
  Campaign as CampaignIcon,
  MoveToInbox as MoveToInboxIcon,
  WhatsApp as WhatsAppIcon,
  SearchRounded as SearchRoundedIcon,
} from "@mui/icons-material";
import SettingsIcon from "@mui/icons-material/Settings";
import PhotoIcon from "@mui/icons-material/Photo";
import { motion, AnimatePresence } from "framer-motion";
import { AuthContext } from "../../AuthContext";
import { db as firestoreDb } from "../../firebase";
import api from "../../axiosConfig";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";

// Sub-components
import {
  consultantTheme,
  HEADER_HEIGHT,
  DRAWER_WIDTH_FULL,
  DRAWER_WIDTH_MINI,
} from "./ConsultantTheme";
import ConsultantInquiryChatView from "./ConsultantChatView";
import {
  MyInquiriesList,
  MyTasksList,
  CustomersList,
} from "./ConsultantTabViews";
import ConsultantPerformanceProfile from "./ConsultantPerformanceProfile";

// --- Imports for Notifications ---
import AICoachWidget from "../Marketing/AICoachWidget";
import useAgencyWorker from "../../hooks/useAgencyWorker";
import UserWorkspaceSettingsDialog, {
  normalizeWorkspaceNotificationPreferences,
} from "../UserWorkspaceSettingsDialog";

// --- Imports for section pages ---
import MassEmailingDashboard from "../../pages/MassEmailingDashboard";
import MyContactsPage from "../../pages/MyContactsPage";
import UnifiedInboxMessagingPage from "../../pages/UnifiedInboxMessagingPage";
import WhatsAppDashboard from "../WhatsAppDashboard";

const menuGroups = [
  {
    label: "MAIN",
    items: [
      { label: "Dashboard", icon: DashboardIcon },
    ],
  },
  {
    label: "WORK",
    items: [
      { label: "My Inquiries", icon: MailOutlineIcon },
      { label: "My Tasks", icon: AssignmentTurnedInIcon },
      { label: "Customers", icon: PeopleAltIcon },
      { label: "My Contacts", icon: PeopleAltIcon },
    ],
  },
  {
    label: "INSIGHTS",
    items: [
      { label: "Performance", icon: QueryStatsIcon },
    ],
  },
  {
    label: "CAMPAIGNS",
    items: [
      { label: "Mass Email", icon: CampaignIcon },
      { label: "Unibox", icon: MoveToInboxIcon },
      { label: "WhatsApp Broadcast", icon: WhatsAppIcon },
    ],
  },
];

/** -------- Settings Dialog (email signature upload) -------- */
export function SettingsDialog({ open, onClose, currentSignatureUrl, onUploaded }) {
  const [preview, setPreview] = useState(currentSignatureUrl || null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setPreview(currentSignatureUrl || null);
  }, [currentSignatureUrl]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!file) {
      onClose();
      return;
    }
    try {
      setBusy(true);
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/users/me/signature", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(res?.data?.signature_url || null);
      onClose();
    } catch (e) {
      console.error(e);
      alert("Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Settings</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Email signature (image)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload an image (PNG/JPG/GIF/WEBP, up to 4 MB). This will be appended
          below your message in emails you send.
        </Typography>

        {preview ? (
          <Box
            sx={{
              p: 1,
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 2,
              mb: 2,
              textAlign: "center",
            }}
          >
            <img
              src={preview}
              alt="signature preview"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              mb: 2,
              textAlign: "center",
              color: "text.secondary",
              border: "1px dashed rgba(0,0,0,0.2)",
            }}
          >
            <PhotoIcon sx={{ fontSize: 32, opacity: 0.6 }} />
            <Typography variant="body2">No signature uploaded yet</Typography>
          </Box>
        )}

        <Button component="label" variant="contained">
          Choose image…
          <input type="file" accept="image/*" hidden onChange={pick} />
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
        <Button onClick={save} variant="contained" disabled={busy || !file}>
          {busy ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ConsultantDashboard() {
  const { logout, user, loading: authLoading } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const isDesktop = useMediaQuery(consultantTheme.breakpoints.up("sm"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMiniDrawer, setIsMiniDrawer] = useState(false);

  const [selectedMenu, setSelectedMenu] = useState("Dashboard");
  const [fetchedRawInquiries, setFetchedRawInquiries] = useState([]);
  const [loadingInquiries, setLoadingInquiries] = useState(true);
  const [viewingInquiryDetail, setViewingInquiryDetail] = useState(null);

  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);

  const [derivedCustomers, setDerivedCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [broadcastBudget, setBroadcastBudget] = useState({ email: null, whatsapp: null });
  const [broadcastBudgetLoading, setBroadcastBudgetLoading] = useState(true);

  // --- State for Dismissed Alert IDs (Persists until next day) ---
  const [dismissedInquiryIds, setDismissedInquiryIds] = useState([]);

  // --- NEW: Profile menu & signature settings (same logic as Marketing) ---
  const [profileEl, setProfileEl] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [notificationPreferences, setNotificationPreferences] = useState(() =>
    normalizeWorkspaceNotificationPreferences()
  );
  const deepLinkHandledRef = useRef("");

  // --- Effect: Load Dismissed Alerts from LocalStorage ---
  useEffect(() => {
    try {
      const stored = localStorage.getItem("consultant_dismissed_alerts");
      if (stored) {
        const parsed = JSON.parse(stored);
        const today = new Date().toDateString();

        // If the stored data is from today, load it. If old, clear it.
        if (parsed.date === today) {
          setDismissedInquiryIds(parsed.ids || []);
        } else {
          localStorage.removeItem("consultant_dismissed_alerts");
          setDismissedInquiryIds([]);
        }
      }
    } catch (e) {
      console.error("Error parsing dismissed alerts", e);
    }
  }, []);

  // --- Effect: Fetch broadcast budget remaining ---
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setBroadcastBudgetLoading(true);
    api.get("/analytics/marketing/broadcast-budget")
      .then((res) => {
        if (cancelled) return;
        const channels = res.data?.data?.channels || res.data?.channels || {};
        setBroadcastBudget({ email: channels.email || null, whatsapp: channels.whatsapp || null });
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setBroadcastBudgetLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  // --- 1. Initialize the Worker (The Brain) ---
  const { alerts, updateData } = useAgencyWorker({
    workerPath: "/agency.worker.js",
    session: {
      role: user?.role || "consultant",
      userId: user?.uid,
    },
  });

  // --- 2. Sync Data with Worker ---
  useEffect(() => {
    if (fetchedRawInquiries.length > 0) {
      updateData({ inquiries: fetchedRawInquiries, leads: [] });
    }
  }, [fetchedRawInquiries, updateData]);

  // --- ONLINE PRESENCE LOGIC ---
  useEffect(() => {
    if (user?.uid) {
      const presenceRef = doc(firestoreDb, "userPresence", user.uid);
      setDoc(
        presenceRef,
        {
          isOnline: true,
          lastSeen: serverTimestamp(),
          role: "consultant",
          displayName: user.displayName || user.email,
        },
        { merge: true }
      );

      return () => {
        setDoc(
          presenceRef,
          {
            isOnline: false,
            lastSeen: serverTimestamp(),
          },
          { merge: true }
        );
      };
    }
  }, [user]);

  // responsive
  useEffect(() => {
    if (!isDesktop) {
      setDrawerOpen(false);
      setIsMiniDrawer(false);
    }
  }, [isDesktop]);

  const handleDrawerToggle = () => {
    if (isDesktop) {
      setIsMiniDrawer((prev) => !prev);
    } else {
      setDrawerOpen((prev) => !prev);
    }
  };

  const consultantDisplayName = useMemo(() => {
    if (!user) return "Consultant";
    return (
      user.name ||
      user.displayName?.split(" ")[0] ||
      user.email?.split("@")[0] ||
      "Consultant"
    );
  }, [user]);

  const resolveInquiryIdentifier = useCallback((inquiryRecord) => {
    return inquiryRecord?.sqlId || inquiryRecord?.firestore_document_id || inquiryRecord?.id || null;
  }, []);

  const syncInquiryApiAction = useCallback(async (inquiryRecord, pathBuilder, payload = null) => {
    const identifier = resolveInquiryIdentifier(inquiryRecord);
    if (!identifier) {
      return false;
    }

    try {
      await api.post(pathBuilder(identifier), payload || {});
      return true;
    } catch (error) {
      console.warn("Consultant inquiry sync failed", error);
      return false;
    }
  }, [resolveInquiryIdentifier]);

  const trackUserActivity = useCallback(async (eventKey, options = {}) => {
    try {
      await api.post("/analytics/activity", {
        event_key: eventKey,
        subject_type: options.subjectType,
        subject_id: options.subjectId,
        subject_label: options.subjectLabel,
        external_subject_id: options.externalSubjectId,
        context_label: options.contextLabel,
        channel: options.channel,
        status: options.status,
        quantity: options.quantity,
        metadata: options.metadata || {},
      });
      return true;
    } catch (error) {
      console.warn("Consultant activity sync failed", error);
      return false;
    }
  }, []);

  // --- NEW: load current signature url for this user (same as Marketing) ---
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/users/me");
        setSignatureUrl(res?.data?.signature_url || null);
        setNotificationPreferences(
          normalizeWorkspaceNotificationPreferences(res?.data?.notification_preferences)
        );
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    if (authLoading || !user || !selectedMenu) {
      return;
    }

    trackUserActivity("ui.dashboard_section_opened", {
      subjectType: "ui",
      subjectLabel: selectedMenu,
      contextLabel: "Consultant dashboard",
      channel: "consultant_dashboard",
      status: String(selectedMenu).toLowerCase().replace(/\s+/g, "_"),
      metadata: {
        source: "consultant_dashboard",
        section: selectedMenu,
      },
    });
  }, [authLoading, selectedMenu, trackUserActivity, user]);

  // --- STRICT FILTERING FOR LIST VIEW ---
  const inquiriesForDisplay = useMemo(() => {
    if (!user || !user.uid) return [];
    const meId = String(user.uid);

    return fetchedRawInquiries
      .filter((inq) => {
        const status = inq.status || "new_chat";
        const isClosed = [
          "closed_by_consultant",
          "closed_by_user",
          "closed_by_provider",
        ].includes(status);

        const consultantId = inq.consultantId ? String(inq.consultantId) : "";
        const consultantName = inq.consultantName
          ? String(inq.consultantName)
          : "";

        // 1. Is it explicitly Mine?
        const isMine = consultantId === meId;

        // 2. Is it truly Pool?
        // FIX: Must have NO ID *AND* NO Name.
        // If SuperAdmin assigned it to "Rutendo" (Name) but ID failed, checking Name prevents it from showing to Brenda.
        const inPool = !consultantId && !consultantName;

        if (isClosed) return isMine;
        return inPool || isMine;
      })
      .sort(
        (a, b) =>
          (b.updatedAt?.toDate?.() || 0) - (a.updatedAt?.toDate?.() || 0)
      );
  }, [fetchedRawInquiries, user]);

  // --- STRICT FILTERING FOR WIDGET (ALERTS) ---
  const widgetInquiries = useMemo(() => {
    if (!user || !user.uid) return [];
    const meId = String(user.uid);

    return fetchedRawInquiries.filter((inq) => {
      const consultantId = inq.consultantId ? String(inq.consultantId) : "";
      const consultantName = inq.consultantName
        ? String(inq.consultantName)
        : "";

      const isMine = consultantId === meId;
      // Same strict pool check here:
      const isUnclaimed = !consultantId && !consultantName;

      // Hide if handled by someone else (Name or ID exists and isn't me)
      if (!isMine && !isUnclaimed) return false;

      // Dismissal Filter
      if (dismissedInquiryIds.includes(inq.id)) return false;

      return true;
    });
  }, [fetchedRawInquiries, user, dismissedInquiryIds]);

  const notificationCount = useMemo(() => {
    let count = alerts.length;

    if (user && user.uid) {
      inquiriesForDisplay.forEach((inq) => {
        const isClosed = [
          "closed_by_consultant",
          "closed_by_user",
          "closed_by_provider",
        ].includes(inq.status);
        if (!isClosed) {
          if (
            String(inq.consultantId) === String(user.uid) &&
            (inq.consultantUnreadCount > 0 || inq.status === "assigned")
          ) {
            count++;
          } else if (
            (!inq.consultantId || inq.consultantId === "") &&
            !inq.consultantName && // Strict check here too
            ["new_chat", "pending_consultant", "assigned"].includes(inq.status)
          ) {
            count++;
          }
        }
      });
    }
    tasks.forEach((task) => {
      if (task.status === "new") count++;
    });
    return count;
  }, [inquiriesForDisplay, tasks, user, alerts]);

  // TASKS
  useEffect(() => {
    if (authLoading || !user || !user.uid) {
      if (!authLoading) setLoadingTasks(false);
      return;
    }
    setLoadingTasks(true);

    const qTasks = query(
      collection(firestoreDb, "consultantTasks"),
      orderBy("assignedAt", "desc")
    );

    const unsubscribe = onSnapshot(
      qTasks,
      (snapshot) => {
        const allTasks = snapshot.docs.map((docL) => ({
          id: docL.id,
          ...docL.data(),
        }));

        const meId = String(user.uid);
        const meEmail = (user.email || "").toLowerCase();
        const myRoleRaw = (user.role || user.department || "")
          .toString()
          .toLowerCase();
        const isConsultantRole = myRoleRaw.includes("consultant");
        const isMarketingRole = myRoleRaw.includes("marketing");

        const filtered = allTasks.filter((task) => {
          const consultantIdField =
            task.consultantId ||
            task.specificConsultantId ||
            task.assignedConsultantId ||
            "";
          const consultantIdStr = consultantIdField
            ? String(consultantIdField).toLowerCase()
            : "";
          const consultantsArray = Array.isArray(task.consultantIds)
            ? task.consultantIds.map((c) => String(c).toLowerCase())
            : [];
          const assigneeEmail = (task.assigneeEmail || "").toLowerCase();
          const meIdLower = meId.toLowerCase();

          const explicitMatch =
            (consultantIdStr &&
              (consultantIdStr === meIdLower || consultantIdStr === meEmail)) ||
            consultantsArray.includes(meIdLower) ||
            consultantsArray.includes(meEmail) ||
            (assigneeEmail && assigneeEmail === meEmail);

          const targetRaw = (task.target ||
            task.department ||
            task.targetGroup ||
            task.targetType ||
            ""
          )
            .toString()
            .toLowerCase();
          const scopeRaw = (task.scope ||
            task.targetScope ||
            task.audience ||
            task.assignScope ||
            task.assignmentScope ||
            ""
          )
            .toString()
            .toLowerCase();

          let targetMatchesMyRole = false;
          if (!targetRaw) {
            targetMatchesMyRole = true;
          } else if (
            isConsultantRole &&
            ["consultant", "consultants", "consultant_team"].includes(targetRaw)
          ) {
            targetMatchesMyRole = true;
          } else if (
            isMarketingRole &&
            ["marketing", "marketing_team", "marketing_department"].includes(
              targetRaw
            )
          ) {
            targetMatchesMyRole = true;
          }

          const isBroadcast =
            !consultantIdStr &&
            consultantsArray.length === 0 &&
            (!scopeRaw ||
              ["all", "everyone", "global", "team", "department", "all_in_department"].includes(
                scopeRaw
              ));
          return explicitMatch || (targetMatchesMyRole && isBroadcast);
        });

        setTasks(filtered);
        setLoadingTasks(false);
      },
      (error) => {
        console.error("Error fetching tasks:", error);
        setLoadingTasks(false);
      }
    );

    return () => unsubscribe();
  }, [user, authLoading]);

  // INQUIRIES
  useEffect(() => {
    if (authLoading || !user || !user.uid) {
      if (!authLoading) setLoadingInquiries(false);
      return;
    }
    setLoadingInquiries(true);

    const relevantStatuses = [
      "new_chat",
      "active_chat",
      "pending_consultant",
      "pending_user_response",
      "assigned",
      "closed_by_consultant",
      "closed_by_provider",
      "closed_by_user",
    ];

    const qInquiries = query(
      collection(firestoreDb, "allInquiries"),
      where("status", "in", relevantStatuses),
      orderBy("updatedAt", "desc")
    );

    const unsubscribe = onSnapshot(
      qInquiries,
      (snapshot) => {
        setFetchedRawInquiries(
          snapshot.docs.map((docL) => ({ id: docL.id, ...docL.data() }))
        );
        setLoadingInquiries(false);
      },
      (error) => {
        console.error("Error fetching inquiries:", error);
        setLoadingInquiries(false);
      }
    );

    return () => unsubscribe();
  }, [user, authLoading]);

  // CUSTOMERS
  useEffect(() => {
    if (selectedMenu !== "Customers" || authLoading || !user) {
      if (!authLoading) setLoadingCustomers(false);
      return;
    }
    setLoadingCustomers(true);

    const customerMap = new Map();
    fetchedRawInquiries
      .filter((inq) => String(inq.consultantId) === String(user.uid))
      .forEach((inq) => {
        if (inq.formData && (inq.formData.email || inq.userId)) {
          const customerKey = inq.userId || inq.formData.email;
          if (!customerMap.has(customerKey)) {
            customerMap.set(customerKey, {
              id: customerKey,
              ...inq.formData,
              inquiryTypes: new Set(),
              lastInteraction:
                inq.updatedAt?.toDate() || inq.createdAt?.toDate(),
              isServiceProvider: inq.inquiryUserType === "service_provider_v2",
            });
          }
          const customerEntry = customerMap.get(customerKey);
          if (inq.type) customerEntry.inquiryTypes.add(inq.type);
          const inquiryDate = inq.updatedAt?.toDate() || inq.createdAt?.toDate();
          if (
            inquiryDate &&
            (!customerEntry.lastInteraction ||
              inquiryDate > customerEntry.lastInteraction)
          ) {
            customerEntry.lastInteraction = inquiryDate;
          }
        }
      });

    const customersList = Array.from(customerMap.values()).map((c) => ({
      ...c,
      inquiryTypes: Array.from(c.inquiryTypes),
    }));
    customersList.sort(
      (a, b) => (b.lastInteraction || 0) - (a.lastInteraction || 0)
    );
    setDerivedCustomers(customersList);
    setLoadingCustomers(false);
  }, [selectedMenu, fetchedRawInquiries, user, authLoading]);

  // keep chat view in sync
  useEffect(() => {
    if (viewingInquiryDetail?.id) {
      const updatedInquiryData = fetchedRawInquiries.find(
        (inq) => inq.id === viewingInquiryDetail.id
      );
      if (updatedInquiryData) {
        if (updatedInquiryData.status !== viewingInquiryDetail.status) {
          setViewingInquiryDetail(updatedInquiryData);
        }
      } else {
        setViewingInquiryDetail(null);
      }
    }
  }, [fetchedRawInquiries, viewingInquiryDetail]);

  // handlers
  const confirmLogout = async () => {
    setExitConfirmOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };

  const handleMenuSelect = (menuLabel) => {
    setSelectedMenu(menuLabel);
    setViewingInquiryDetail(null);
    if (!isDesktop) setDrawerOpen(false);
  };

  const handleInquiryRowClick = useCallback(async (inquiry) => {
    if (!user || !user.uid) return;

    if (
      inquiry.consultantId &&
      String(inquiry.consultantId) !== "" &&
      String(inquiry.consultantId) !== String(user.uid)
    ) {
      setSnackbarMessage(
        `This chat is already being handled by ${
          inquiry.consultantName || "another consultant"
        }.`
      );
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    let inquiryToOpen = { ...inquiry };
    const isPoolInquiry = !inquiry.consultantId || inquiry.consultantId === "";

    if (isPoolInquiry) {
      try {
        const inquiryDocRef = doc(firestoreDb, "allInquiries", inquiry.id);
        const claimUpdates = {
          consultantId: String(user.uid),
          consultantName: consultantDisplayName,
          status: "active_chat",
          assignedAt: serverTimestamp(),
          consultantUnreadCount: 0,
          claimedBy: user.uid,
          isUrgent: inquiry.isUrgent || false,
          viewers: [],
        };
        await updateDoc(inquiryDocRef, claimUpdates);
        await syncInquiryApiAction(inquiry, (identifier) => `/api/inquiries/${identifier}/claim`, {
          consultantId: user.uid,
        });
        setSnackbarMessage("Inquiry claimed successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        inquiryToOpen = {
          ...inquiryToOpen,
          ...claimUpdates,
          assignedAt: new Date(),
        };
      } catch (error) {
        console.error("Error claiming inquiry:", error);
        setSnackbarMessage("Could not claim inquiry. It may have been taken.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
    } else if (String(inquiry.consultantId) === String(user.uid)) {
      if (inquiry.consultantUnreadCount > 0) {
        updateDoc(doc(firestoreDb, "allInquiries", inquiry.id), {
          consultantUnreadCount: 0,
        }).catch((err) => console.error("Error clearing unread count:", err));
        inquiryToOpen.consultantUnreadCount = 0;
      }
    }

    setViewingInquiryDetail(inquiryToOpen);
    setSelectedMenu("My Inquiries");
    syncInquiryApiAction(
      inquiryToOpen,
      (identifier) => `/api/inquiries/${identifier}/activity`,
      {
        event_key: "ui.conversation_opened",
        channel: inquiryToOpen.channel || inquiryToOpen.origin || "web",
        metadata: {
          source: "consultant_dashboard",
          firestore_inquiry_id: inquiryToOpen.id,
        },
      }
    );
    if (!isDesktop) setDrawerOpen(false);
  }, [
    consultantDisplayName,
    isDesktop,
    syncInquiryApiAction,
    user,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openInquiry = params.get("openInquiry");

    if (!openInquiry) {
      return;
    }

    if (deepLinkHandledRef.current === openInquiry) {
      return;
    }

    const targetInquiry = fetchedRawInquiries.find((inq) => {
      const candidates = [
        inq.id,
        inq.originalInquiryId,
        inq.sqlId,
        inq.firestore_document_id,
      ].filter(Boolean).map(String);

      return candidates.includes(String(openInquiry));
    });

    if (!targetInquiry) {
      return;
    }

    deepLinkHandledRef.current = String(openInquiry);
    navigate("/consultant", { replace: true });
    void handleInquiryRowClick(targetInquiry);
  }, [fetchedRawInquiries, handleInquiryRowClick, location.search, navigate]);

  const handleMarkInquiryClosed = async (inquiryToClose) => {
    if (!inquiryToClose || !user || !user.uid) return;
    const inquiryDocRef = doc(firestoreDb, "allInquiries", inquiryToClose.id);
    try {
      const updates = {
        status: "closed_by_consultant",
        closedAt: serverTimestamp(),
        closedBy: user.uid,
        consultantName: consultantDisplayName,
        consultantUnreadCount: 0,
        userUnreadCount: 0,
      };
      await updateDoc(inquiryDocRef, updates);
      await syncInquiryApiAction(
        inquiryToClose,
        (identifier) => `/api/inquiries/respond/${identifier}`,
        {
          status: "Closed",
        }
      );
      setSnackbarMessage(
        `Inquiry with ${
          inquiryToClose.formData?.fullName || "customer"
        } closed.`
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error closing inquiry:", error);
      setSnackbarMessage(`Error closing inquiry: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleMarkTaskCompleted = async (task) => {
    if (!task || !task.id) return;
    const consultantTaskRef = doc(firestoreDb, "consultantTasks", task.id);
    try {
      await updateDoc(consultantTaskRef, {
        status: "completed",
        completed: true,
        completedAt: serverTimestamp(),
        completedById: user.uid,
        completedByName: consultantDisplayName,
      });
      if (task.superadminTaskId) {
        const superadminTaskRef = doc(
          firestoreDb,
          "superadminTasks",
          task.superadminTaskId
        );
        await updateDoc(superadminTaskRef, {
          status: "completed",
          completedAt: serverTimestamp(),
          completedBy: user.uid,
          completedByName: consultantDisplayName,
        });
      }
      await trackUserActivity("tasks.completed", {
        subjectType: "task",
        subjectLabel: task.taskDescription || "Consultant task",
        externalSubjectId: task.superadminTaskId || task.id,
        channel: "consultant_dashboard",
        status: "completed",
        metadata: {
          source: "consultant_dashboard",
          firestore_task_id: task.id,
          superadmin_task_id: task.superadminTaskId || null,
          assigned_by: task.assignedByName || task.createdByName || task.assignedBy || task.createdBy || null,
        },
      });
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? {
                ...t,
                status: "completed",
                completed: true,
                completedAt: new Date(),
                completedById: user.uid,
                completedByName: consultantDisplayName,
              }
            : t
        )
      );
      setSnackbarMessage("Task marked as completed.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error updating task:", error);
      setSnackbarMessage(`Error updating task: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleCustomerView = async (customer) => {
    if (!customer) {
      return;
    }

    await trackUserActivity("ui.customer_viewed", {
      subjectType: "customer",
      subjectLabel: customer.fullName || customer.email || customer.phone || "Customer",
      externalSubjectId: customer.id || customer.email || customer.phone || null,
      channel: "consultant_customers",
      status: customer.isServiceProvider ? "service_provider" : "customer",
      metadata: {
        source: "consultant_dashboard",
        customer_id: customer.id || null,
        email: customer.email || null,
        phone: customer.phone || null,
      },
    });
  };

  // --- WIDGET HANDLER (Dismissal Logic) ---
  const handleWidgetAlertClick = (alert) => {
    const rawId = alert.id.replace(
      /^(new_|hot_|urgent_|ghost_|urgent_unclaimed_|hot_tracking_|lead_)/,
      ""
    );
    const inquiry = fetchedRawInquiries.find((i) => String(i.id) === String(rawId));
    if (inquiry) {
      // A. Open the chat
      handleInquiryRowClick(inquiry);

      // B. Dismiss Alert (Add to state & local storage)
      const newDismissed = [...dismissedInquiryIds, inquiry.id];
      setDismissedInquiryIds(newDismissed);

      localStorage.setItem(
        "consultant_dismissed_alerts",
        JSON.stringify({
          date: new Date().toDateString(),
          ids: newDismissed,
        })
      );
    } else {
      setSnackbarMessage("Inquiry details not found.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
    }
  };

  const currentDrawerWidth = isDesktop
    ? isMiniDrawer
      ? DRAWER_WIDTH_MINI
      : DRAWER_WIDTH_FULL
    : DRAWER_WIDTH_FULL;
  const showCompactRail = isDesktop && isMiniDrawer;
  const isInboxWorkspace = selectedMenu === "Unibox";
  const isEdgeToEdgeWorkspace =
    isInboxWorkspace ||
    selectedMenu === "Mass Email" ||
    selectedMenu === "WhatsApp Broadcast";
  const consultantInboxBadgeCount = inquiriesForDisplay.length;
  const consultantTaskBadgeCount = tasks.filter(
    (task) => !task?.isCompleted && !task?.completedAt
  ).length;
  const consultantMenuBadgeCounts = {
    "My Inquiries": consultantInboxBadgeCount,
    "My Tasks": consultantTaskBadgeCount,
    Unibox: notificationCount,
  };
  const consultantShellSubtitle = viewingInquiryDetail
    ? "Customer details, ownership, and response context are ready."
    : selectedMenu === "Unibox"
    ? "Use the same unified messaging workspace as the main line."
    : "A shared consultant workspace for service, tasks, and follow-ups.";

  const renderContent = () => {
    if (viewingInquiryDetail && selectedMenu === "My Inquiries") {
      return (
        <motion.div
          key="chat-view"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{ height: "100%" }}
        >
          <ConsultantInquiryChatView
            inquiry={viewingInquiryDetail}
            onBack={() => setViewingInquiryDetail(null)}
            consultant={user}
            onMarkClosed={handleMarkInquiryClosed}
          />
        </motion.div>
      );
    }

    switch (selectedMenu) {
      case "Dashboard":
        return (
          <Box sx={{ p: 2, height: "100%" }}>
            {/* Budget remaining summary */}
            {!broadcastBudgetLoading && (broadcastBudget.email || broadcastBudget.whatsapp) ? (
              <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
                {broadcastBudget.email ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.8, borderRadius: 2, bgcolor: "#EEF4FF", border: "1px solid #C7D9F7" }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#1565c0" }}>
                      Email remaining:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 900, color: broadcastBudget.email.remaining > 0 ? "#1565c0" : "#b91c1c" }}>
                      {broadcastBudget.email.remaining === null ? "No cap" : Number(broadcastBudget.email.remaining).toLocaleString()}
                    </Typography>
                  </Box>
                ) : null}
                {broadcastBudget.whatsapp ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.8, borderRadius: 2, bgcolor: "#EDFAF4", border: "1px solid #A8DEC9" }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#128C7E" }}>
                      WhatsApp remaining:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 900, color: broadcastBudget.whatsapp.remaining > 0 ? "#128C7E" : "#b91c1c" }}>
                      {broadcastBudget.whatsapp.remaining === null ? "No cap" : Number(broadcastBudget.whatsapp.remaining).toLocaleString()}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            ) : null}
            <AICoachWidget
              // Uses the STRICT FILTERED LIST (checks name + ID)
              inquiries={widgetInquiries}
              user={user}
              hotLeads={widgetInquiries.map((inq) => ({
                ...inq,
                score:
                  inq.isUrgent ||
                  inq.priority === "high" ||
                  inq.leadStatus === "hot"
                    ? 85
                    : 15,
              }))}
              onAlertClick={handleWidgetAlertClick}
            />
          </Box>
        );
      case "My Inquiries":
        return (
          <MyInquiriesList
            inquiries={inquiriesForDisplay}
            loading={loadingInquiries}
            user={user}
            onRowClick={handleInquiryRowClick}
          />
        );
      case "My Tasks":
        return (
          <MyTasksList
            tasks={tasks}
            loading={loadingTasks}
            onMarkComplete={handleMarkTaskCompleted}
          />
        );
      case "My Contacts":
        return <MyContactsPage />;
      case "Customers":
        return (
          <CustomersList
            customers={derivedCustomers}
            loading={loadingCustomers}
            onCustomerView={handleCustomerView}
          />
        );
      case "Performance":
        return <ConsultantPerformanceProfile />;
      case "Mass Email":
        return <MassEmailingDashboard />;
      case "Unibox":
        return (
          <UnifiedInboxMessagingPage
            shellExpandedSidebar={isDesktop && !showCompactRail}
          />
        );
      case "WhatsApp Broadcast":
        return <WhatsAppDashboard />;
      default:
        return (
          <Typography sx={{ p: 2 }}>
            Welcome, {consultantDisplayName}!
          </Typography>
        );
    }
  };

  if (authLoading && !user) {
    return (
      <ThemeProvider theme={consultantTheme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </ThemeProvider>
    );
  }

  if (!user && !authLoading) {
    return (
      <ThemeProvider theme={consultantTheme}>
        <CssBaseline />
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h5">Access Denied</Typography>
          <Typography>Please log in.</Typography>
          <Button variant="contained" href="/login" sx={{ mt: 2 }}>
            Login
          </Button>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={consultantTheme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          minHeight: 0,
          minWidth: 0,
          overflow: "hidden",
          background:
            "radial-gradient(circle at top right, rgba(34, 197, 94, 0.18), transparent 24%), linear-gradient(180deg, #f9fcf9 0%, #eef6ef 100%)",
        }}
      >
        <AppBar
          position="fixed"
          sx={{
            zIndex: consultantTheme.zIndex.drawer + 1,
            display: isInboxWorkspace ? "none" : undefined,
            transition: consultantTheme.transitions.create(["width", "margin"], {
              easing: consultantTheme.transitions.easing.sharp,
              duration: consultantTheme.transitions.duration.leavingScreen,
            }),
            bgcolor: "rgba(255,255,255,0.82)",
            backdropFilter: "blur(18px)",
            borderBottom: "1px solid rgba(22, 101, 52, 0.08)",
            ...(isDesktop && {
              width: `calc(100% - ${currentDrawerWidth}px)`,
              ml: `${currentDrawerWidth}px`,
            }),
          }}
        >
          <Toolbar sx={{ minHeight: HEADER_HEIGHT, height: HEADER_HEIGHT, px: { xs: 1.5, sm: 2.5 }, gap: 1.25 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                mr: 0.5,
                color: "text.primary",
                width: 40,
                height: 40,
                border: "1px solid rgba(22, 101, 52, 0.08)",
                bgcolor: "rgba(255,255,255,0.86)",
              }}
            >
              <MenuIcon />
            </IconButton>

            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{ fontWeight: 700, color: "#163126", letterSpacing: "-0.02em" }}
              >
                {viewingInquiryDetail
                  ? viewingInquiryDetail.formData?.fullName || "Chat"
                  : selectedMenu}
              </Typography>
              <Typography
                variant="caption"
                noWrap
                sx={{ display: "block", color: "rgba(22, 49, 38, 0.66)", letterSpacing: "0.01em" }}
              >
                {consultantShellSubtitle}
              </Typography>
            </Box>

            <IconButton
              color="inherit"
              sx={{
                color: "text.primary",
                width: 40,
                height: 40,
                border: "1px solid rgba(22, 101, 52, 0.08)",
                bgcolor: "rgba(255,255,255,0.86)",
              }}
            >
              <SearchRoundedIcon fontSize="small" />
            </IconButton>

            <Tooltip title="Notifications">
              <IconButton
                color="inherit"
                sx={{
                  color: "text.primary",
                  width: 40,
                  height: 40,
                  border: "1px solid rgba(22, 101, 52, 0.08)",
                  bgcolor: "rgba(255,255,255,0.86)",
                }}
              >
                <Badge badgeContent={notificationCount} color="error" invisible={!notificationCount}>
                  <NotificationsActiveIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* PROFILE BUTTON (now opens menu like marketing) */}
            <Tooltip title={user?.email || "Profile"}>
              <Button
                color="inherit"
                onClick={(e) => setProfileEl(e.currentTarget)}
                sx={{
                  textTransform: "none",
                  color: "text.primary",
                  fontWeight: 500,
                  flexShrink: 0,
                  minWidth: { xs: "auto", sm: 0 },
                  px: { xs: 0.8, sm: 1.25 },
                  py: 0.7,
                  borderRadius: 999,
                  border: "1px solid rgba(22, 101, 52, 0.08)",
                  bgcolor: "rgba(255,255,255,0.86)",
                }}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  variant="dot"
                  color="success"
                  sx={{ mr: { xs: 0, sm: 1 } }}
                >
                  <Avatar
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: "#2db838",
                    }}
                  >
                    {consultantDisplayName?.charAt(0).toUpperCase()}
                  </Avatar>
                </Badge>
                <Box
                  component="span"
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    flexDirection: "column",
                    alignItems: "flex-start",
                    lineHeight: 1.1,
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 700, color: "#163126" }}>
                    {consultantDisplayName}
                  </Box>
                  <Box component="span" sx={{ color: "rgba(22, 49, 38, 0.62)", fontSize: 12 }}>
                    Consultant Desk
                  </Box>
                </Box>
              </Button>
            </Tooltip>

            {/* PROFILE MENU (Settings + Logout) */}
            <Menu
              anchorEl={profileEl}
              open={Boolean(profileEl)}
              onClose={() => setProfileEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => {
                  setSettingsOpen(true);
                  setProfileEl(null);
                }}
              >
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                Settings
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setProfileEl(null);
                  setExitConfirmOpen(true);
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Drawer
          variant={isDesktop ? "permanent" : "temporary"}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            width: currentDrawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: currentDrawerWidth,
              boxSizing: "border-box",
              transition: consultantTheme.transitions.create("width", {
                easing: consultantTheme.transitions.easing.sharp,
                duration: consultantTheme.transitions.duration.enteringScreen,
              }),
              overflowX: "hidden",
              background: "linear-gradient(180deg, #ffffff 0%, #f6fbf7 100%)",
              borderRight: "1px solid rgba(22, 101, 52, 0.08)",
              boxShadow: "16px 0 38px -34px rgba(15, 23, 42, 0.22)",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <Box
            sx={{
              minHeight: HEADER_HEIGHT + 6,
              display: "flex",
              alignItems: "center",
              justifyContent: showCompactRail ? "center" : "flex-start",
              px: showCompactRail ? 1 : 2,
              py: 1.6,
              borderBottom: "1px solid rgba(22, 101, 52, 0.08)",
            }}
          >
            {showCompactRail ? (
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: "#2db838",
                  fontWeight: 700,
                  fontSize: 22,
                  boxShadow: "0 10px 24px -16px rgba(45, 184, 56, 0.95)",
                }}
              >
                A
              </Avatar>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  width: "100%",
                }}
              >
                <Avatar
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 1.6,
                    bgcolor: "#2db838",
                    fontWeight: 700,
                    fontSize: 24,
                    boxShadow: "0 10px 24px -16px rgba(45, 184, 56, 0.95)",
                  }}
                >
                  A
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      color: "#163126",
                      letterSpacing: "-0.04em",
                      lineHeight: 1.1,
                      fontSize: "1.18rem",
                    }}
                  >
                    Abisinya
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(22, 49, 38, 0.62)" }}>
                    Consultant Desk
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {showCompactRail ? (
            <Box sx={{ py: 1.5, display: "flex", justifyContent: "center" }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                variant="dot"
                color="success"
              >
                <Avatar sx={{ width: 36, height: 36, bgcolor: "#dbeafe", color: "#163126", fontWeight: 700 }}>
                  {consultantDisplayName?.charAt(0).toUpperCase()}
                </Avatar>
              </Badge>
            </Box>
          ) : (
            <Box sx={{ px: 1.5, pb: 1.2 }}>
              <Box
                sx={{
                  px: 1.4,
                  py: 1.2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                  borderRadius: 2.5,
                  border: "1px solid rgba(22, 101, 52, 0.08)",
                  bgcolor: "rgba(255,255,255,0.82)",
                  boxShadow: "0 14px 34px -30px rgba(15, 23, 42, 0.2)",
                }}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  variant="dot"
                  color="success"
                  sx={{
                    "& .MuiBadge-badge": {
                      boxShadow: "0 0 0 2px rgba(255,255,255,0.96)",
                    },
                  }}
                >
                  <Avatar sx={{ width: 38, height: 38, bgcolor: "#eef6ef", color: "#163126", fontWeight: 700 }}>
                    {consultantDisplayName?.charAt(0).toUpperCase()}
                  </Avatar>
                </Badge>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} noWrap sx={{ color: "#163126" }}>
                    {user?.email || consultantDisplayName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    Consultant Team
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          <List sx={{ p: showCompactRail ? "10px 0" : "6px 0", flexGrow: 1, overflowY: "auto" }}>
            {menuGroups.map((group, gi) => (
              <React.Fragment key={group.label}>
                {/* Divider between groups */}
                {gi > 0 && (
                  <Divider sx={{ my: "8px", mx: showCompactRail ? 1 : 2, borderColor: "rgba(22, 101, 52, 0.08)" }} />
                )}
                {/* Section label — hidden in mini/rail mode */}
                {!showCompactRail && (
                  <Box
                    sx={{
                      px: 2.75,
                      pt: gi === 0 ? "8px" : "4px",
                      pb: "6px",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "rgba(22, 49, 38, 0.54)",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                    }}
                  >
                    {group.label}
                  </Box>
                )}
                {group.items.map(({ label, icon: Icon }) => {
                  const isActive = selectedMenu === label && !viewingInquiryDetail;
                  const badgeCount = consultantMenuBadgeCounts[label];

                  return (
                    <Tooltip key={label} title={showCompactRail ? label : ""} placement="right">
                      <ListItemButton
                        selected={isActive}
                        onClick={() => handleMenuSelect(label)}
                        sx={{
                          minHeight: showCompactRail ? 48 : 44,
                          width: showCompactRail ? 48 : "auto",
                          justifyContent: showCompactRail ? "center" : "flex-start",
                          px: showCompactRail ? 0 : 1.75,
                          py: showCompactRail ? 1.25 : 1.1,
                          mx: showCompactRail ? "auto" : 1.25,
                          mb: 0.5,
                          borderRadius: 3.5,
                          color: isActive ? "#ffffff" : "#163126",
                          fontWeight: isActive ? 600 : 500,
                          boxShadow: isActive ? "0 16px 28px -20px rgba(45, 184, 56, 0.95)" : "none",
                          "&.Mui-selected": {
                            bgcolor: "#2db838",
                            color: "#ffffff",
                            "&:hover": { bgcolor: "#24952e" },
                            "& .MuiSvgIcon-root": { color: "#ffffff" },
                          },
                          "&:hover": { bgcolor: isActive ? "#24952e" : "rgba(45, 184, 56, 0.08)" },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: showCompactRail ? 0 : 1.35,
                            justifyContent: "center",
                            color: "inherit",
                          }}
                        >
                          <Icon />
                        </ListItemIcon>
                        <ListItemText
                          primary={label}
                          primaryTypographyProps={{ fontSize: 15, fontWeight: isActive ? 600 : 500 }}
                          sx={{ opacity: showCompactRail ? 0 : 1, width: showCompactRail ? 0 : "auto", m: 0 }}
                        />
                        {!showCompactRail && badgeCount > 0 ? (
                          <Box
                            component="span"
                            sx={{
                              minWidth: 24,
                              height: 24,
                              px: 0.9,
                              borderRadius: 999,
                              bgcolor: isActive ? "rgba(255,255,255,0.18)" : "rgba(45, 184, 56, 0.14)",
                              color: isActive ? "#ffffff" : "#16a34a",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 12,
                              fontWeight: 700,
                            }}
                          >
                            {badgeCount > 99 ? "99+" : badgeCount}
                          </Box>
                        ) : null}
                      </ListItemButton>
                    </Tooltip>
                  );
                })}
              </React.Fragment>
            ))}
          </List>

          {isDesktop ? (
            <Box sx={{ p: showCompactRail ? 1 : 1.5, borderTop: "1px solid rgba(22, 101, 52, 0.08)" }}>
              <Button
                fullWidth
                onClick={handleDrawerToggle}
                sx={{
                  minWidth: 0,
                  borderRadius: 999,
                  px: showCompactRail ? 0 : 1.4,
                  py: 1,
                  color: "#5f7468",
                  justifyContent: showCompactRail ? "center" : "flex-start",
                  border: "1px solid rgba(22, 101, 52, 0.08)",
                  bgcolor: "rgba(255,255,255,0.78)",
                }}
              >
                {showCompactRail ? <MenuIcon fontSize="small" /> : "Collapse"}
              </Button>
            </Box>
          ) : null}
        </Drawer>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: "transparent",
            p: isEdgeToEdgeWorkspace ? 0 : { xs: 1.25, sm: 2, md: 2.5 },
            minHeight: 0,
            minWidth: 0,
            height: "100vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {!isInboxWorkspace && (
            <Toolbar sx={{ height: HEADER_HEIGHT, minHeight: HEADER_HEIGHT }} />
          )}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              overflowX: "hidden",
              display: "flex",
              flexDirection: "column",
              borderRadius: isEdgeToEdgeWorkspace ? 0 : 4,
              background: isEdgeToEdgeWorkspace
                ? "transparent"
                : "linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(249,252,249,0.92) 100%)",
              boxShadow: isEdgeToEdgeWorkspace
                ? "none"
                : "0 32px 60px -48px rgba(15, 23, 42, 0.28)",
              border: isEdgeToEdgeWorkspace ? "none" : "1px solid rgba(22, 101, 52, 0.08)",
            }}
          >
            <AnimatePresence mode="wait">{renderContent()}</AnimatePresence>
          </Box>
        </Box>

        {/* Settings dialog (email signature upload) */}
        <UserWorkspaceSettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          currentSignatureUrl={signatureUrl}
          notificationPreferences={notificationPreferences}
          workspaceUserId={user?.id || user?.uid || null}
          onUploaded={(url) => {
            setSignatureUrl(url);
            setSnackbarMessage("Signature saved");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
          }}
          onPreferencesSaved={(preferences) => {
            setNotificationPreferences(normalizeWorkspaceNotificationPreferences(preferences));
            setSnackbarMessage("Notification settings saved");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
          }}
        />

        <Dialog open={exitConfirmOpen} onClose={() => setExitConfirmOpen(false)}>
          <DialogTitle>Confirm Logout</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to log out?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExitConfirmOpen(false)}>Cancel</Button>
            <Button onClick={confirmLogout} color="error" autoFocus>
              Logout
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%", borderRadius: 2, boxShadow: 6 }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}
